import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Link2,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
  ShieldQuestion,
} from 'lucide-react';
import { ACS_AUTH_MODES, type AcsAuthMode, type AcsAuthStatus } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { Button, Card, ErrorNotice, Pill, Spinner } from '../../components/ui';

/**
 * Who the ACS lets in — rebuilt around the decision, not the setting.
 *
 * The first version of this tab was three rows with a button each, and its
 * buttons said the opposite of what they did: "Turn off" sat on the OPEN row,
 * because open is authentication turned off — a reading that requires already
 * knowing the answer. Worse, one click applied a fleet-wide change with no
 * confirmation, on the one control in Settings that can take every subscriber
 * off management at once.
 *
 * The rebuild is one flow with three stages:
 *
 *   1. WHAT IS IN FORCE — the live expression read back out of GenieACS, not
 *      the mode this platform believes it saved. Those are two facts and the
 *      gap between them is the dangerous one, so falling out of sync is a
 *      banner with its own re-apply, not a footnote.
 *   2. THE THREE RUNGS — selected, not applied. Picking one shows what would
 *      change, with the live numbers: which logins the door would accept, how
 *      many informing devices the strictest rung would refuse today and where
 *      the fix for them lives.
 *   3. APPLY — a single deliberate step that restates the consequence it was
 *      shown with. Going stricter carries the warning; going to open is the
 *      escape hatch and is never made harder than one confirm.
 */

const RUNGS: Record<
  AcsAuthMode,
  {
    label: string;
    icon: typeof ShieldOff;
    /** What the door does, in one sentence an operator can repeat. */
    means: string;
    /** What applying it changes, shown at selection AND restated on apply. */
    consequence: string;
  }
> = {
  open: {
    label: 'Open',
    icon: ShieldOff,
    means: 'Every device that reaches the ACS is accepted — any credentials, or none.',
    consequence:
      'Nothing is refused. This is the escape hatch: if a stricter rung ever locks the fleet out, this is where to come back to.',
  },
  credentials: {
    label: 'Organization credentials',
    icon: ShieldCheck,
    means:
      'Only an inform username and password belonging to an organization on this platform. The credential is what identifies the tenant.',
    consequence:
      'Every ONU still carrying an older ACS password stops being manageable the moment this applies — it is refused at the door and cannot be reached to fix remotely.',
  },
  inventory: {
    label: 'Credentials and inventory',
    icon: ShieldQuestion,
    means:
      'The credentials rule, and the serial must also resolve to an ONU that organization actually has. A leaked credential admits nobody.',
    consequence:
      'Any informing device that cannot be matched to an ONU is refused — including subscribers whose router reports a different serial than their chassis knows.',
  },
};

export function AcsAuthTab() {
  const queryClient = useQueryClient();
  const { can } = useSession();
  const canManage = can('infra.manage');

  const query = useQuery({
    queryKey: ['settings', 'acs-auth'],
    queryFn: () => api.get<AcsAuthStatus>('/settings/acs-auth'),
  });

  /** The rung being LOOKED AT. Selecting is free; only Apply changes anything. */
  const [selected, setSelected] = useState<AcsAuthMode | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (mode: AcsAuthMode) => api.put<AcsAuthStatus>('/settings/acs-auth', { mode }),
    onSuccess: (next, mode) => {
      queryClient.setQueryData(['settings', 'acs-auth'], next);
      void queryClient.invalidateQueries({ queryKey: ['settings', 'acs-auth'] });
      setSelected(null);
      setNotice(
        mode === 'open'
          ? 'Open — every device is accepted again, from its next inform.'
          : `${RUNGS[mode].label} is in force. It applies to each device at its next inform — nothing restarts.`,
      );
    },
  });

  if (query.isLoading) {
    return (
      <Card>
        <div className="flex justify-center p-10">
          <Spinner />
        </div>
      </Card>
    );
  }
  if (query.isError) return <ErrorNotice error={query.error} />;

  const status = query.data!;
  const inForce = RUNGS[status.mode];
  const InForceIcon = inForce.icon;
  /** What the panel below describes: the selection, else what is in force. */
  const shown = selected ?? status.mode;
  const changing = selected !== null && selected !== status.mode;

  return (
    <div className="grid gap-4">
      {notice && (
        <div className="flex items-start gap-2 rounded-lg border border-success/30 bg-success-soft px-4 py-3 text-sm text-success">
          <Check className="mt-0.5 size-4 shrink-0" />
          <span>{notice}</span>
        </div>
      )}
      {save.isError && <ErrorNotice error={save.error} />}

      {/* ------------------------------------------------ what is in force */}
      <Card
        title={
          <span className="flex items-center gap-2">
            <InForceIcon className="size-4 text-muted" />
            ACS authentication
          </span>
        }
        action={<Pill state tone={status.mode === 'open' ? 'warn' : 'success'}>{inForce.label}</Pill>}
      >
        <div className="grid gap-3 p-5">
          <p className="max-w-prose text-sm text-ink">{inForce.means}</p>
          <p className="max-w-prose text-xs text-muted">
            One rule, applied by GenieACS to every device on every chassis at its next inform.
            Changing it never restarts anything.
          </p>

          {/*
            The LIVE expression, read back out of GenieACS itself. The saved
            mode and the enforced rule are two different facts; this row is
            always the second one, so an operator never has to wonder which
            they are looking at.
          */}
          <div className="rounded-lg border border-line bg-surface-2 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wide text-faint">
              Enforced by GenieACS right now
            </div>
            <div className="mt-1 font-mono text-xs">
              {status.published ? (
                <code className="break-all">{status.published}</code>
              ) : (
                <span className="text-warn">no rule — every device is accepted</span>
              )}
            </div>
          </div>

          {!status.inSync && (
            <div className="flex items-start justify-between gap-3 rounded-lg border border-warn/30 bg-warn-soft px-3 py-2.5 text-sm text-warn">
              <span className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <span>
                  The rule GenieACS is enforcing is not the one this page has recorded — somebody
                  edited the ACS directly, or a change half-landed. The fleet is subject to what is
                  LIVE, above. Re-applying makes the two agree again.
                </span>
              </span>
              <Button
                size="sm"
                variant="secondary"
                icon={<RefreshCw className="size-4" />}
                disabled={!canManage}
                loading={save.isPending}
                onClick={() => save.mutate(status.mode)}
              >
                Re-apply
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* ------------------------------------------------------- the rungs */}
      <Card title="Change who is let in">
        <div className="grid gap-4 p-5">
          {/*
            A ladder read left to right, strictest last. Picking a rung only
            selects it — the panel underneath says what would change, and Apply
            is its own deliberate step. The old version applied on this click,
            which put a fleet-wide switch one slip away.
          */}
          <div className="grid gap-2 sm:grid-cols-3">
            {ACS_AUTH_MODES.map((mode) => {
              const rung = RUNGS[mode];
              const Icon = rung.icon;
              const active = status.mode === mode;
              const isShown = shown === mode;
              const uninstallable = mode === 'inventory' && !status.inventoryAvailable;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setSelected(mode === status.mode ? null : mode)}
                  className={`rounded-lg border px-3.5 py-3 text-left transition-colors ${
                    isShown ? 'border-primary bg-primary-soft' : 'border-line hover:border-faint'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon className="size-4 shrink-0 text-muted" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">{rung.label}</span>
                  </span>
                  <span className="mt-1.5 block">
                    {active ? (
                      <Pill tone="success">
                        <Check className="size-3" />
                        in force
                      </Pill>
                    ) : uninstallable ? (
                      <Pill tone="neutral">not installed</Pill>
                    ) : (
                      <span className="text-xs text-faint">
                        {mode === 'open' ? 'the way back' : 'stricter'}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ------------------------------------- what the shown rung means */}
          <div className="rounded-lg border border-line bg-surface-2 p-4">
            <p className="text-sm text-ink">{RUNGS[shown].means}</p>

            {/* The door's keys, whenever a credential rung is on screen. */}
            {shown !== 'open' && (
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-wide text-faint">
                  Logins this door accepts
                </div>
                {status.credentials.length === 0 ? (
                  <p className="mt-1 text-sm text-danger">
                    No organization has an inform username and password set — this rung would refuse
                    the entire fleet, and the API refuses to apply it. Set inform credentials on a
                    TR-069 profile first.
                  </p>
                ) : (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {status.credentials.map((c) => (
                      <Pill key={c.orgId} tone="neutral">
                        <span className="font-mono">{c.username}</span>
                        <span className="text-faint">· {c.orgName}</span>
                      </Pill>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* The strictest rung's own two prerequisites, with live numbers. */}
            {shown === 'inventory' && (
              <div className="mt-3 grid gap-2">
                {!status.inventoryAvailable && (
                  <p className="flex items-start gap-2 text-sm text-warn">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    The lookup extension is not installed in the ACS container. Turning this on
                    without it would refuse every device, so the API refuses to — run the installer
                    (deploy/install.sh) to add it.
                  </p>
                )}
                {status.unmatchedDevices > 0 ? (
                  <p className="flex items-start gap-2 text-sm text-warn">
                    <Link2 className="mt-0.5 size-4 shrink-0" />
                    <span>
                      <strong>{status.unmatchedDevices}</strong>{' '}
                      {status.unmatchedDevices === 1 ? 'device' : 'devices'} informing today cannot
                      be matched to an ONU and would be refused on this rung. Most are routers that
                      report a different serial than their chassis knows — pair them first with{' '}
                      <em>Link ACS devices</em> on the OLT&rsquo;s page, then this number falls.
                    </span>
                  </p>
                ) : (
                  <p className="flex items-start gap-2 text-sm text-success">
                    <Check className="mt-0.5 size-4 shrink-0" />
                    Every device informing today matches an ONU — nothing currently manageable would
                    be refused.
                  </p>
                )}
              </div>
            )}

            {/* ------------------------------------------------------ apply */}
            {changing && (
              <div className="mt-4 border-t border-line pt-3">
                <p className="flex items-start gap-2 text-sm">
                  <ArrowRight className="mt-0.5 size-4 shrink-0 text-muted" />
                  <span className={selected === 'open' ? 'text-muted' : 'text-warn'}>
                    {RUNGS[selected!].consequence}
                  </span>
                </p>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    /*
                      Colour states the direction: loosening to open is the
                      recovery move and reads calm; tightening is the one that
                      can strand ONUs and reads like it.
                    */
                    variant={selected === 'open' ? 'secondary' : 'danger'}
                    disabled={
                      !canManage ||
                      (selected === 'inventory' && !status.inventoryAvailable) ||
                      (selected !== 'open' && status.credentials.length === 0)
                    }
                    loading={save.isPending}
                    onClick={() => {
                      setNotice(null);
                      save.mutate(selected!);
                    }}
                  >
                    {selected === 'open' ? 'Switch to open' : `Enforce ${RUNGS[selected!].label.toLowerCase()}`}
                  </Button>
                </div>
              </div>
            )}
            {!changing && selected === null && status.mode !== 'open' && (
              <p className="mt-3 border-t border-line pt-3 text-xs text-faint">
                Pick a rung above to see what switching would change. Open stays one click away —
                it is the way back if anything stops informing.
              </p>
            )}
          </div>
        </div>
      </Card>

      <InformIntervalCard />
    </div>
  );
}

/** The choices an operator actually picks between, said in their own units. */
const INFORM_CHOICES: Array<{ seconds: number; label: string }> = [
  { seconds: 60, label: 'Every minute' },
  { seconds: 120, label: 'Every 2 minutes' },
  { seconds: 300, label: 'Every 5 minutes' },
  { seconds: 600, label: 'Every 10 minutes' },
  { seconds: 900, label: 'Every 15 minutes' },
  { seconds: 1800, label: 'Every 30 minutes' },
  { seconds: 3600, label: 'Every hour' },
];

function informLabel(seconds: number): string {
  const known = INFORM_CHOICES.find((c) => c.seconds === seconds);
  if (known) return known.label;
  return seconds % 60 === 0 ? `Every ${seconds / 60} minutes` : `Every ${seconds} seconds`;
}

/**
 * How often every CPE reports in.
 *
 * The number's real home is GenieACS's own `inform` provision — the card
 * reads it back from there and writes it back there, so a hand-edit in the
 * GenieACS UI shows up here instead of being fought. One number for the whole
 * fleet, and each CPE picks a change up the next time it reports on the OLD
 * interval, which is why the save notice says when convergence completes.
 */
function InformIntervalCard() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['acs-inform'],
    queryFn: () => api.get<{ seconds: number }>('/settings/acs-inform'),
  });

  const save = useMutation({
    mutationFn: (seconds: number) =>
      api.put<{ seconds: number }>('/settings/acs-inform', { seconds }),
    onSuccess: async (status) => {
      await queryClient.invalidateQueries({ queryKey: ['acs-inform'] });
      setSelected(null);
      setNotice(
        `${informLabel(status.seconds)} from each device's next report — the whole fleet is on the new rhythm within one old interval.`,
      );
    },
  });

  const current = query.data?.seconds ?? null;
  /** A hand-set value that is none of the presets still deserves a row. */
  const choices =
    current !== null && !INFORM_CHOICES.some((c) => c.seconds === current)
      ? [...INFORM_CHOICES, { seconds: current, label: informLabel(current) }].sort(
          (a, b) => a.seconds - b.seconds,
        )
      : INFORM_CHOICES;
  const shown = selected ?? current;
  const changed = selected !== null && selected !== current;

  return (
    <Card>
      <div className="p-5">
        <div className="mb-1 flex items-center gap-2">
          <RefreshCw className="size-4 text-muted" />
          <h2 className="text-sm font-semibold text-ink">Inform interval</h2>
          {current !== null && <Pill tone="primary">{informLabel(current)}</Pill>}
        </div>
        <p className="mb-4 text-sm text-muted">
          How often every CPE reports to the ACS. This is what keeps the online/offline reading,
          WAN addresses and WiFi details fresh — and it is also the ACS&apos;s load: halving the
          interval doubles the informs the whole fleet sends.
        </p>

        {query.isLoading ? (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        ) : query.isError ? (
          <ErrorNotice error={query.error} />
        ) : (
          <>
            {notice && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-success/30 bg-success-soft px-4 py-3 text-sm text-success">
                <Check className="mt-0.5 size-4 shrink-0" />
                <span>{notice}</span>
              </div>
            )}
            {save.isError && <ErrorNotice error={save.error} />}
            <div className="flex flex-wrap gap-2">
              {choices.map((c) => (
                <button
                  key={c.seconds}
                  onClick={() => {
                    setNotice(null);
                    setSelected(c.seconds === current ? null : c.seconds);
                  }}
                  className={
                    shown === c.seconds
                      ? 'rounded-lg border border-primary bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary'
                      : 'rounded-lg border border-line px-3 py-1.5 text-sm text-muted hover:text-ink'
                  }
                >
                  {c.label}
                </button>
              ))}
            </div>
            {changed && (
              <div className="mt-4 flex items-center justify-between gap-4 border-t border-line pt-3">
                <p className="text-sm text-muted">
                  {informLabel(selected!)} for every CPE, each switching at its next report on the
                  current {informLabel(current ?? 0).toLowerCase().replace('every ', '')} rhythm.
                </p>
                <Button
                  variant="primary"
                  loading={save.isPending}
                  onClick={() => save.mutate(selected!)}
                >
                  Apply
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
